'use client';

import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountError,
  useBlockPart,
  useCart,
  useCommerceSource,
  tx,
  PortalLink,
  type AccountAddress,
  type AccountOrderSummary,
  type AccountProfile,
} from '@akademi/site-builder-runtime';
import {
  AdapterPlaceholder,
  BlockRoot,
  formatDecimalMoney,
  sharedStyles,
} from '@akademi/site-builder-blocks';
import { statusLabel } from '../commerce/orderViews';
import { addressSummary } from './addressDraft';
import {
  AccountField,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  signInHrefFor,
  titleStyle,
  useAccountErrorText,
  useSignInRedirect,
} from './accountShared';
import { InstallerCertificatesUpload, TradeDocumentsUpload } from './applyAtSignUp';
import { installerErrorKind, type InstallerCommerceSource, type InstallerMe } from './installer';
import { useInstallerWords, type Localized as InstallerLocalized } from './installerParts';
import {
  tradeApplicationsOn,
  tradeErrorKind,
  tradeRequirementsOf,
  tradeSavingsOf,
  type TradeAccountSummary,
  type TradeApplicationStatus,
  type TradeCommerceSource,
} from './tradeApplication';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/** Orders fetched per page. Enough for most shoppers' whole history in one go. */
const PAGE_SIZE = 10;

/**
 * "My account": who you are, what you have ordered, and a way out.
 *
 * <p>Signed out, it says so and offers the way in — it does not redirect. A
 * redirect from a page someone bookmarked is a page that flickers and vanishes;
 * a sentence and a button is a page that explains itself.
 *
 * <p>Signing out also forgets the basket in this browser. A basket started while
 * signed in belongs to the account, and leaving it behind would give the next
 * person at a shared computer a basket they cannot check out and did not fill.
 */
export interface AccountDashboardProps {
  title?: Localized;
  signInHref?: string;
  registerHref?: string;
  /** The order detail page; the order number is appended as `?number=`. */
  orderHref?: string;
  /** The address book page. */
  addressesHref?: string;
  continueHref?: string;
  /** The trade account application page; offered on a shop that takes applications. */
  tradeHref?: string;
  /**
   * Pages an approved trade customer is linked to from the trade section, each only when the shop
   * has one: its quotes, its saved (reorder) lists, and its quick order. Orders are this page's own.
   */
  quotesHref?: string;
  savedListsHref?: string;
  quickOrderHref?: string;
  /** What the shop calls its installers, e.g. "fitter", for the installer section. */
  installerNoun?: InstallerLocalized;
  installerNounPlural?: InstallerLocalized;
}

export function AccountDashboard({
  title,
  signInHref = '/sign-in',
  registerHref = '/register',
  orderHref = '/account-order',
  addressesHref = '/account-addresses',
  continueHref = '/categories',
  tradeHref = '/trade',
  quotesHref,
  savedListsHref,
  quickOrderHref,
  installerNoun,
  installerNounPlural,
}: AccountDashboardProps) {
  const commerce = useCommerceSource();
  const cart = useCart();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');

  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!commerce?.accountProfile) {
      setChecked(true);
      return;
    }
    commerce
      .accountProfile()
      .then((found) => {
        if (!cancelled) setProfile(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  const here = typeof window === 'undefined' ? '/account' : window.location.pathname;
  // Only once the check has settled, and only when it settled on "no profile": `failed` means the
  // shop could not be asked, and the shopper may well be signed in.
  useSignInRedirect(checked && !failed && !profile, signInHref, here);

  if (!commerce?.accountProfile) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="AccountDashboard"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function signOut() {
    if (!commerce?.signOut || signingOut) return;
    setSigningOut(true);
    try {
      await commerce.signOut();
    } catch {
      // The BFF clears the cookie even when revoking upstream fails, so the
      // browser is signed out either way; carry on to the reload.
    }
    cart?.forget();
    window.location.assign('/');
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 900 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.dashboardTitle')}
        </h1>

        {!checked && <p style={mutedStyle}>{t('commerce.account.loading')}</p>}

        {checked && failed && (
          <p role="alert" style={errorStyle}>
            {t('commerce.account.errors.UNAVAILABLE')}
          </p>
        )}

        {checked && !failed && !profile && (
          <div style={{ display: 'grid', gap: 16, justifyItems: 'start' }}>
            <p style={leadStyle}>{t('commerce.account.signedOutRedirecting')}</p>
            <PortalLink
              href={signInHrefFor(signInHref, here)}
              style={{ ...buttonStyle, textDecoration: 'none' }}
            >
              {t('commerce.account.signIn')}
            </PortalLink>
            <span style={mutedStyle}>
              {t('commerce.account.noAccount')}{' '}
              <PortalLink href={registerHref}>{t('commerce.account.registerLink')}</PortalLink>
            </span>
          </div>
        )}

        {checked && profile && (
          <div style={{ display: 'grid', gap: 40 }}>
            <div style={headerRowStyle}>
              <p style={{ ...leadStyle, margin: 0 }}>
                {t('commerce.account.greeting', { name: profile.firstName || profile.email })}
              </p>
              <button
                type="button"
                onClick={() => void signOut()}
                disabled={signingOut}
                style={secondaryButtonStyle}
              >
                {signingOut ? t('commerce.account.signingOut') : t('commerce.account.signOut')}
              </button>
            </div>

            <AccountOrders orderHref={orderHref} continueHref={continueHref} />

            {commerce.accountAddresses && <AccountAddresses addressesHref={addressesHref} />}

            {(commerce as TradeCommerceSource).myTradeApplication && (
              <AccountTrade
                tradeHref={tradeHref}
                links={{ quotesHref, savedListsHref, quickOrderHref }}
              />
            )}

            {(commerce as InstallerCommerceSource).installerMe && (
              <AccountInstaller noun={installerNoun} nounPlural={installerNounPlural} />
            )}

            <ProfileEditor profile={profile} onSaved={setProfile} />
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

function AccountOrders({ orderHref, continueHref }: { orderHref: string; continueHref: string }) {
  const commerce = useCommerceSource();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const locale = i18n.language || 'en';

  const [orders, setOrders] = useState<AccountOrderSummary[]>([]);
  const [page, setPage] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (nextPage: number) => {
      if (!commerce?.accountOrders) return;
      setLoading(true);
      try {
        const batch = await commerce.accountOrders(nextPage, PAGE_SIZE);
        setOrders((previous) => (nextPage === 0 ? batch : [...previous, ...batch]));
        setPage(nextPage);
        setMore(batch.length === PAGE_SIZE);
        setFailed(false);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [commerce],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  const placed = (value: string | null | undefined) => {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value));
    } catch {
      return value.slice(0, 10);
    }
  };

  return (
    <section aria-labelledby="account-orders-heading">
      <h2 id="account-orders-heading" style={subheadingStyle}>
        {t('commerce.account.ordersTitle')}
      </h2>
      {failed && (
        <p role="alert" style={errorStyle}>
          {t('commerce.account.errors.UNAVAILABLE')}
        </p>
      )}
      {!failed && !loading && orders.length === 0 && (
        <p style={mutedStyle}>
          {t('commerce.account.noOrders')}{' '}
          <PortalLink href={continueHref}>{t('commerce.cart.continue')}</PortalLink>
        </p>
      )}
      {orders.length > 0 && (
        <ul style={orderListStyle}>
          {orders.map((order) => (
            <li key={order.orderNumber} style={orderRowStyle}>
              <div style={{ display: 'grid', gap: 2 }}>
                <PortalLink
                  href={`${orderHref}?number=${encodeURIComponent(order.orderNumber)}`}
                  style={{ fontWeight: 600 }}
                >
                  {order.orderNumber}
                </PortalLink>
                <span style={{ ...mutedStyle, fontSize: 13 }}>
                  {[placed(order.placedAt), t('commerce.account.itemCount', { count: order.lineCount })]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              <span style={statusPillStyle}>
                {statusLabel(t, order.fulfilmentStatus || order.status || undefined)}
              </span>
              <span style={{ fontWeight: 600 }}>
                {formatDecimalMoney(order.total, order.currency ?? '', locale)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {loading && <p style={mutedStyle}>{t('commerce.account.loading')}</p>}
      {more && !loading && (
        <button type="button" onClick={() => void load(page + 1)} style={secondaryButtonStyle}>
          {t('commerce.account.moreOrders')}
        </button>
      )}
    </section>
  );
}

/** The default address in a line, and the way to the book. The book itself is its own page. */
function AccountAddresses({ addressesHref }: { addressesHref: string }) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('site-builder-blocks');
  const [book, setBook] = useState<AccountAddress[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    commerce
      ?.accountAddresses?.()
      .then((found) => {
        if (!cancelled) setBook(found);
      })
      .catch(() => {
        // The link still works; the page it leads to reports its own failure.
        if (!cancelled) setBook([]);
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  const preferred = book?.find((address) => address.defaultAddress);

  return (
    <section aria-labelledby="account-addresses-heading">
      <h2 id="account-addresses-heading" style={subheadingStyle}>
        {t('commerce.account.addressesTitle')}
      </h2>
      {book === null ? (
        <p style={mutedStyle}>{t('commerce.account.loading')}</p>
      ) : (
        <p style={mutedStyle}>
          {preferred
            ? `${t('commerce.account.defaultAddressLead')} ${addressSummary(preferred)}`
            : t('commerce.account.noDefaultAddress')}
        </p>
      )}
      <PortalLink href={addressesHref} style={{ display: 'inline-block', marginTop: 12 }}>
        {t('commerce.account.manageAddresses')}
      </PortalLink>
    </section>
  );
}

/**
 * The shopper's trade account: where their application stands, or the way to apply.
 *
 * <p>Nothing at all on a shop that takes no applications — the feature is off unless the shop
 * switches it on — and nothing until that is known, so the section never appears and vanishes.
 *
 * <p>A shopper who is a user of an approved account sees the account itself instead: its number,
 * the saving its prices give, its terms, whether it is on hold, its members for its admin, and the
 * ways to buy again. That shows whether or not the shop still takes new applications.
 */
function AccountTrade({ tradeHref, links }: { tradeHref: string; links: TradeLinks }) {
  const commerce = useCommerceSource() as TradeCommerceSource | null;
  const { t } = useTranslation('site-builder-blocks');
  const [state, setState] = useState<
    | { kind: 'checking' }
    | { kind: 'off' }
    | { kind: 'failed' }
    | { kind: 'account'; account: TradeAccountSummary }
    | { kind: 'ready'; application: TradeApplicationStatus | null }
  >({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    const source = commerce;
    if (!source?.myTradeApplication) return;
    void (async () => {
      if (source.myTradeAccount) {
        try {
          const account = await source.myTradeAccount();
          if (account) {
            if (!cancelled) setState({ kind: 'account', account });
            return;
          }
        } catch {
          // The account cannot be read just now; where the application stands still can.
        }
      }
      if (!(await tradeApplicationsOn(source))) {
        if (!cancelled) setState({ kind: 'off' });
        return;
      }
      try {
        const application = await source.myTradeApplication!();
        if (!cancelled) setState({ kind: 'ready', application });
      } catch (failure) {
        if (!cancelled) setState({ kind: tradeErrorKind(failure) === 'NOT_FOUND' ? 'off' : 'failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (state.kind === 'checking' || state.kind === 'off') return null;
  if (state.kind === 'account') return <TradeAccountPanel account={state.account} links={links} />;

  const application = state.kind === 'ready' ? state.application : null;
  const statusText = application ? t(`commerce.trade.dashboard.status.${application.status}`) : null;

  return (
    <section aria-labelledby="account-trade-heading">
      <h2 id="account-trade-heading" style={subheadingStyle}>
        {t('commerce.trade.dashboard.title')}
      </h2>
      {state.kind === 'failed' && <p style={mutedStyle}>{t('commerce.trade.dashboard.unavailable')}</p>}
      {state.kind === 'ready' && !application && (
        <p style={mutedStyle}>
          {t('commerce.trade.dashboard.none')}{' '}
          <PortalLink href={tradeHref}>{t('commerce.trade.dashboard.apply')}</PortalLink>
        </p>
      )}
      {application && (
        <div style={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
          <span style={statusPillStyle}>{statusText}</span>
          <p style={mutedStyle}>{t(`commerce.trade.dashboard.lead.${application.status}`)}</p>
          {application.status === 'REJECTED' && application.reason && (
            <p style={mutedStyle}>{t('commerce.trade.dashboard.reason', { reason: application.reason })}</p>
          )}
          {application.status === 'REJECTED' && (
            <PortalLink href={tradeHref}>{t('commerce.trade.dashboard.applyAgain')}</PortalLink>
          )}
          {application.status === 'PENDING' && application.documentsOutstanding && (
            <OutstandingDocuments
              onUploaded={(status) => setState({ kind: 'ready', application: status })}
            />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The proof documents a pending application still waits for — one made with the sign-up — and the
 * way to upload them here. The shop's requirements say which documents it takes.
 */
function OutstandingDocuments({ onUploaded }: { onUploaded: (status: TradeApplicationStatus) => void }) {
  const commerce = useCommerceSource() as TradeCommerceSource | null;
  const { t } = useTranslation('site-builder-blocks');
  const [requirements, setRequirements] = useState<Awaited<ReturnType<typeof tradeRequirementsOf>> | null>(null);
  const [open, setOpen] = useState(false);

  async function start() {
    if (!commerce) return;
    setRequirements(await tradeRequirementsOf(commerce));
    setOpen(true);
  }

  return (
    <div style={{ display: 'grid', gap: 8, justifyItems: 'start', width: '100%' }}>
      <p role="status" style={noticeStyle}>
        {t('commerce.trade.dashboard.documentsOutstanding')}
      </p>
      {commerce?.uploadTradeDocuments && !open && (
        <button type="button" onClick={() => void start()} style={secondaryButtonStyle}>
          {t('commerce.signUpApply.documents.submit')}
        </button>
      )}
      {commerce?.uploadTradeDocuments && open && requirements && (
        <TradeDocumentsUpload commerce={commerce} requirements={requirements} outstanding onDone={onUploaded} />
      )}
    </div>
  );
}

/**
 * The shopper as one of the shop's installers — "fitters" on a kitchen shop — from their profile:
 * where their application or account stands, the areas they cover, what stops them quoting, the
 * certificates an application still waits for with the way to upload them, and the documents page.
 * Nothing at all for a shopper who is neither installer nor applicant.
 */
function AccountInstaller({ noun, nounPlural }: { noun?: InstallerLocalized; nounPlural?: InstallerLocalized }) {
  const commerce = useCommerceSource() as InstallerCommerceSource | null;
  const { t } = useTranslation('site-builder-blocks');
  const words = useInstallerWords(noun, nounPlural);
  const [state, setState] = useState<{ kind: 'checking' } | { kind: 'none' } | { kind: 'failed' } | { kind: 'ready'; me: InstallerMe }>(
    { kind: 'checking' },
  );

  useEffect(() => {
    let cancelled = false;
    if (!commerce?.installerMe) return;
    commerce
      .installerMe()
      .then((me) => {
        if (!cancelled) setState(me ? { kind: 'ready', me } : { kind: 'none' });
      })
      .catch((failure: unknown) => {
        const kind = installerErrorKind(failure);
        if (!cancelled) setState({ kind: kind === 'NOT_INSTALLER' || kind === 'NOT_SIGNED_IN' ? 'none' : 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (state.kind === 'checking' || state.kind === 'none') return null;
  const heading = (
    <h2 id="account-installer-heading" style={subheadingStyle}>
      {t('commerce.installer.dashboard.title', { ...words })}
    </h2>
  );
  if (state.kind === 'failed') {
    return (
      <section aria-labelledby="account-installer-heading">
        {heading}
        <p style={mutedStyle}>{t('commerce.installer.dashboard.unavailable', { ...words })}</p>
      </section>
    );
  }

  const { me } = state;
  const outstanding = me.certificatesOutstanding ?? [];
  const policies = outstanding.map((type) => t(`commerce.installer.policies.${type}`)).join(', ');
  const blockers = me.blockers.filter((blocker) => !(blocker === 'SUSPENDED' && me.state === 'SUSPENDED'));
  return (
    <section aria-labelledby="account-installer-heading">
      {heading}
      <div style={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
        <span style={statusPillStyle}>{t(`commerce.installer.dashboard.status.${me.state}`)}</span>
        <p style={mutedStyle}>{t(`commerce.installer.dashboard.lead.${me.state}`, { ...words })}</p>
        {me.areas.length > 0 && (
          <p style={mutedStyle}>{t('commerce.installer.dashboard.areas', { areas: me.areas.join(', ') })}</p>
        )}
        {blockers.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((blocker) => (
              <li key={blocker}>
                {t(`commerce.installer.dashboard.blockers.${blocker}`, {
                  ...words,
                  defaultValue: t('commerce.installer.dashboard.blockers.OTHER', { ...words }),
                })}
              </li>
            ))}
          </ul>
        )}
        {me.state === 'APPLIED' && outstanding.length > 0 && (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.installer.dashboard.certificatesOutstanding', { policies })}
            </p>
            {commerce?.uploadInstallerCertificates && (
              <InstallerCertificatesUpload
                commerce={commerce}
                types={outstanding}
                onDone={(status) =>
                  setState({ kind: 'ready', me: { ...me, certificatesOutstanding: status?.certificatesOutstanding ?? [] } })
                }
              />
            )}
          </>
        )}
        {me.documentsLink && (
          <PortalLink href={me.documentsLink}>{t('commerce.installer.dashboard.documentsLink')}</PortalLink>
        )}
      </div>
    </section>
  );
}

interface TradeLinks {
  quotesHref?: string;
  savedListsHref?: string;
  quickOrderHref?: string;
}

/** The terms, in words: "30 days from invoice", "pay with order". */
function termsText(t: (key: string, values?: Record<string, unknown>) => string, account: TradeAccountSummary) {
  const type = account.termsType ?? '';
  const days = account.termsDays ?? 0;
  if (type === 'PROFORMA' || type === 'CASH') return t(`commerce.trade.summary.terms.${type}`);
  if ((type === 'DAYS_FROM_INVOICE' || type === 'END_OF_MONTH') && days > 0) {
    return t(`commerce.trade.summary.terms.${type}`, { days });
  }
  return null;
}

/** An approved trade account, as its own user sees it. */
function TradeAccountPanel({ account, links }: { account: TradeAccountSummary; links: TradeLinks }) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const locale = i18n.language || 'en';
  const money = (value: string | null) =>
    value && Number(value) > 0 ? formatDecimalMoney(value, account.currency ?? '', locale) : null;
  const credit = money(account.creditLimit);
  const terms = termsText(t, account);
  const minimum = money(account.minOrderValue);
  const savings = tradeSavingsOf(account.savings);
  const members = account.role === 'admin' ? account.members ?? [] : [];
  const band = t(`commerce.trade.summary.bands.${account.band}`);
  const buyAgain: Array<[string, string | undefined]> = [
    [t('commerce.trade.summary.links.orders'), '#account-orders-heading'],
    [t('commerce.trade.summary.links.quotes'), links.quotesHref],
    [t('commerce.trade.summary.links.savedLists'), links.savedListsHref],
    [t('commerce.trade.summary.links.quickOrder'), links.quickOrderHref],
  ];

  return (
    <section aria-labelledby="account-trade-heading">
      <h2 id="account-trade-heading" style={subheadingStyle}>
        {t('commerce.trade.dashboard.title')}
      </h2>
      <div style={{ display: 'grid', gap: 12 }}>
        {account.onStop && (
          <div role="alert" style={errorStyle}>
            {t('commerce.trade.summary.onStop')}
            {account.onStopReason && (
              <> {t('commerce.trade.summary.onStopReason', { reason: account.onStopReason })}</>
            )}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={statusPillStyle}>{t('commerce.trade.dashboard.status.APPROVED')}</span>
          <span style={{ fontWeight: 600 }}>{account.company}</span>
        </div>
        <dl style={factsStyle}>
          <dt style={factTermStyle}>{t('commerce.trade.summary.number')}</dt>
          <dd style={factValueStyle}>{account.accountNumber}</dd>
          <dt style={factTermStyle}>{t('commerce.trade.summary.prices')}</dt>
          <dd style={factValueStyle}>{band.startsWith('commerce.') ? account.band : band}</dd>
          {savings && (
            <>
              <dt style={factTermStyle}>{t('commerce.trade.summary.saving')}</dt>
              <dd style={factValueStyle}>
                {t('commerce.trade.summary.savingText', {
                  typical: savings.typicalPct,
                  upTo: savings.upToPct,
                  products: new Intl.NumberFormat(locale).format(savings.products),
                })}
              </dd>
            </>
          )}
          {(credit || terms) && (
            <>
              <dt style={factTermStyle}>{t('commerce.trade.summary.credit')}</dt>
              <dd style={factValueStyle}>
                {[credit ? t('commerce.trade.summary.creditLimit', { amount: credit }) : null, terms]
                  .filter(Boolean)
                  .join(' · ')}
              </dd>
            </>
          )}
          {minimum && (
            <>
              <dt style={factTermStyle}>{t('commerce.trade.summary.minimumOrder')}</dt>
              <dd style={factValueStyle}>{minimum}</dd>
            </>
          )}
          {account.vatExempt && (
            <>
              <dt style={factTermStyle}>{t('commerce.trade.summary.vat')}</dt>
              <dd style={factValueStyle}>{t('commerce.trade.summary.vatExempt')}</dd>
            </>
          )}
        </dl>
        <p style={mutedStyle}>{t('commerce.trade.dashboard.lead.APPROVED')}</p>
        <ul aria-label={t('commerce.trade.summary.links.title')} style={linkListStyle}>
          {buyAgain
            .filter((entry): entry is [string, string] => !!entry[1])
            .map(([label, href]) => (
              <li key={label}>
                <PortalLink href={href}>{label}</PortalLink>
              </li>
            ))}
        </ul>
        {members.length > 0 && (
          <div>
            <h3 style={{ margin: '8px 0', fontSize: 16 }}>{t('commerce.trade.summary.members')}</h3>
            <ul style={orderListStyle}>
              {members.map((member) => (
                <li key={member.email} style={memberRowStyle}>
                  <span style={{ fontWeight: 600 }}>{member.name || member.email}</span>
                  <span style={mutedStyle}>{member.name ? member.email : ''}</span>
                  <span style={statusPillStyle}>
                    {(() => {
                      const role = t(`commerce.trade.summary.roles.${member.role}`);
                      return role.startsWith('commerce.') ? member.role : role;
                    })()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function ProfileEditor({
  profile,
  onSaved,
}: {
  profile: AccountProfile;
  onSaved: (profile: AccountProfile) => void;
}) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.updateAccountProfile || busy) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      onSaved(
        await commerce.updateAccountProfile({ firstName: firstName.trim(), lastName: lastName.trim() }),
      );
      setSaved(true);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="account-profile-heading">
      <h2 id="account-profile-heading" style={subheadingStyle}>
        {t('commerce.account.profileTitle')}
      </h2>
      <EmailRow email={profile.email} />
      <form onSubmit={(event) => void save(event)} style={formStyle}>
        {error && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        {saved && (
          <p role="status" style={noticeStyle}>
            {t('commerce.account.profileSaved')}
          </p>
        )}
        <AccountField
          id="profile-first-name"
          label={t('commerce.account.firstName')}
          value={firstName}
          onChange={setFirstName}
          autoComplete="given-name"
          required
        />
        <AccountField
          id="profile-last-name"
          label={t('commerce.account.lastName')}
          value={lastName}
          onChange={setLastName}
          autoComplete="family-name"
          required
        />
        {commerce?.updateAccountProfile && (
          <button type="submit" disabled={busy} style={buttonStyle}>
            {busy ? t('commerce.account.saving') : t('commerce.account.saveProfile')}
          </button>
        )}
      </form>
    </section>
  );
}

/**
 * The sign-in email, and the way to change it.
 *
 * <p>Its own form, beside the name form rather than inside it: the change needs
 * the current password and only takes effect from a link sent to the new
 * address, so it cannot be saved with a first name.
 */
function EmailRow({ email }: { email: string }) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.requestEmailChange || busy) return;
    const address = newEmail.trim();
    setBusy(true);
    setError(null);
    try {
      await commerce.requestEmailChange({ newEmail: address, password });
      setSentTo(address);
      setOpen(false);
      setNewEmail('');
      setPassword('');
    } catch (failure) {
      const kind = failure instanceof AccountError ? failure.kind : null;
      setError(
        kind === 'INVALID_CREDENTIALS'
          ? t('commerce.account.emailChange.wrongPassword')
          : kind === 'EMAIL_TAKEN'
            ? t('commerce.account.emailChange.taken')
            : errorText(failure),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 12, maxWidth: 420, marginBottom: 20 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{t('commerce.account.email')}</span>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12 }}>
          <span style={{ overflowWrap: 'anywhere' }}>{email}</span>
          {commerce?.requestEmailChange && !open && (
            <button
              type="button"
              onClick={() => {
                setOpen(true);
                setSentTo(null);
                setError(null);
              }}
              style={linkButtonStyle}
            >
              {t('commerce.account.emailChange.change')}
            </button>
          )}
        </span>
      </div>
      {sentTo && (
        <p role="status" style={noticeStyle}>
          {t('commerce.account.emailChange.sent', { email: sentTo })}
        </p>
      )}
      {open && (
        <form
          onSubmit={(event) => void send(event)}
          aria-labelledby="email-change-heading"
          style={{ ...formStyle, padding: 16, border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 'var(--radius-md, 8px)' }}
        >
          <h3 id="email-change-heading" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            {t('commerce.account.emailChange.title')}
          </h3>
          <p style={{ ...mutedStyle, fontSize: 14 }}>{t('commerce.account.emailChange.intro')}</p>
          {error && (
            <p role="alert" style={errorStyle}>
              {error}
            </p>
          )}
          <AccountField
            id="email-change-new"
            label={t('commerce.account.emailChange.newEmail')}
            value={newEmail}
            onChange={setNewEmail}
            type="email"
            autoComplete="email"
            required
          />
          <AccountField
            id="email-change-password"
            label={t('commerce.account.emailChange.currentPassword')}
            value={password}
            onChange={setPassword}
            type="password"
            autoComplete="current-password"
            required
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button type="submit" disabled={busy} style={buttonStyle}>
              {busy ? t('commerce.account.emailChange.sending') : t('commerce.account.emailChange.send')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              style={secondaryButtonStyle}
            >
              {t('commerce.account.emailChange.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const linkButtonStyle: CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  fontSize: 14,
  color: 'var(--color-primary, #2563eb)',
  textDecoration: 'underline',
  cursor: 'pointer',
};
const headerRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
};
const subheadingStyle: CSSProperties = { margin: '0 0 16px', fontSize: 20, fontWeight: 600 };
const orderListStyle: CSSProperties = { listStyle: 'none', margin: '0 0 16px', padding: 0 };
const orderRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
  alignItems: 'center',
  gap: 16,
  padding: '14px 0',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};
const statusPillStyle: CSSProperties = {
  fontSize: 13,
  padding: '2px 10px',
  borderRadius: 999,
  background: 'var(--color-surface-muted, #f3f4f6)',
  whiteSpace: 'nowrap',
};
const factsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, max-content) minmax(0, 1fr)',
  gap: '6px 16px',
  margin: 0,
};
const factTermStyle: CSSProperties = { color: 'var(--color-text-muted, #6b7280)', fontSize: 14 };
const factValueStyle: CSSProperties = { margin: 0 };
const linkListStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '8px 20px', listStyle: 'none', margin: 0, padding: 0 };
const memberRowStyle: CSSProperties = { ...orderRowStyle, gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto' };
